import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IcCheck, IcLock, IcUnlock, IcCalculator, IcClose } from '../theme/icons';
import Swal from '../theme/swal';
import { gated, GATE_CANCELLED } from '../services/identityGate';
import reservationService from '../services/reservationService';
import KI from '../theme/palette';

// Shared action buttons for a product reservation, so the Réservations page, the
// dashboard "Réservation de produits" card, and the mobile home all behave the
// same. `reservation` needs at least { id, status, product_name, quantity }
// (and `product` when available). `onChanged` is called to refresh the caller
// after an action that stays on the page. `dark` tunes the neutral/outline
// buttons for dark backgrounds (mobile home).
export default function ReservationProductActions({ reservation: r, onChanged, dark = false, className = '' }) {
  const navigate = useNavigate();
  const refresh = () => { if (onChanged) onChanged(); };

  const changeStatus = async (status) => {
    try { if ((await gated('Qui fait ce changement ?', () => reservationService.setStatus(r.id, status))) === GATE_CANCELLED) return; refresh(); }
    catch (err) {
      const d = err?.response?.data;
      const msg = (Array.isArray(d?.status) ? d.status[0] : d?.status)
        || d?.detail || (typeof d === 'string' ? d : null)
        || 'Impossible de mettre à jour la réservation.';
      Swal.fire('Erreur', msg, 'error');
    }
  };

  const handleCancel = async (e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: 'Annuler la réservation ?',
      text: `${r.customer_name || ''} — ${r.product_name}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Annuler la réservation', cancelButtonText: 'Retour',
      confirmButtonColor: KI.coralDark,
    });
    if (res.isConfirmed) changeStatus('cancelled');
  };

  const handleArrived = (e) => { e?.stopPropagation?.(); changeStatus('arrived'); };

  const handleBlock = async (e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: 'Bloquer le produit pour le client ?',
      html: `<b>${r.quantity}</b> × ${r.product_name} sera retiré du stock et réservé pour <b>${r.customer_name}</b>.`,
      showCancelButton: true,
      confirmButtonText: 'Bloquer', cancelButtonText: 'Annuler',
      // Sélection Studio (owner 2026-09-09) : plus d'icône « ? » ; « Bloquer »
      // au kit Secondaire bordé encre (.btn-s), « Annuler » au kit Ghost abandon
      // (.btn-g), rayon 6. buttonsStyling:false retire `swal2-styled`, donc les
      // couleurs globales de index.css (peacock / gris) ne s'appliquent plus ici.
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn-s px-4 py-2 rounded-md text-sm mx-1',
        cancelButton: 'btn-g px-4 py-2 rounded-md text-sm mx-1',
      },
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui bloque le produit ?', () => reservationService.blockReservation(r.id))) === GATE_CANCELLED) return;
      refresh();
      Swal.fire({ icon: 'success', title: 'Produit bloqué', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible de bloquer le produit.', 'error');
    }
  };

  const handleUnblock = async (e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: 'Débloquer le produit ?',
      html: `<b>${r.quantity}</b> × ${r.product_name} sera remis en stock (réservation « Arrivée »).`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Débloquer', cancelButtonText: 'Annuler', confirmButtonColor: KI.ramps.amber[600],
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui débloque le produit ?', () => reservationService.unblockReservation(r.id))) === GATE_CANCELLED) return;
      refresh();
      Swal.fire({ icon: 'success', title: 'Produit débloqué', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible de débloquer le produit.', 'error');
    }
  };

  // Open "Nouvelle vente" with the reserved product in the panier and the client
  // preselected only if they're a fidélité member (resolved server-side).
  const handleSell = async (e) => {
    e?.stopPropagation?.();
    if (r.product === null) {
      Swal.fire('Produit hors-catalogue', 'Ce produit doit être vendu depuis la page Vente.', 'info');
      return;
    }
    try {
      const prep = await reservationService.prepareSale(r.id);
      navigate('/seller-sales', {
        state: {
          addProductId: prep.product_id,
          productQty: prep.quantity,
          clientId: prep.customer_id,
          reservationId: prep.reservation_id,
          // Avance encaissée à la réservation — le panier la déduit du reste à payer.
          reservationDeposit: prep.deposit_amount,
        },
      });
    } catch (err) {
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible d’ouvrir la vente.', 'error');
    }
  };

  // « Marquer arrivée » n'est pressable que si le produit est réellement entré
  // en stock (facture saisie) : stock connu et suffisant pour la réservation.
  // Hors-catalogue (product_stock null/absent) : pas de stock à vérifier.
  const stockKnown = r.product_stock !== undefined && r.product_stock !== null;
  const canArrive = !stockKnown || (Number(r.product_stock) || 0) >= (r.quantity || 1);

  const base = 'px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors';
  // « Annuler » : btn-g dans les deux thèmes — sur fond sombre le conteneur
  // porte `bdg-on-dark` et l'équivalence automatique (index.css §06) s'applique.
  const neutral = 'btn-g';
  const amber = dark
    ? 'border border-amber-300/70 text-amber-200 hover:bg-amber-400/10'
    : 'border border-amber-300 text-amber-700 hover:bg-amber-50';

  return (
    <div className={`flex flex-wrap gap-2 ${dark ? 'bdg-on-dark ' : ''}${className}`}>
      {r.status === 'pending' && (
        <button
          onClick={handleArrived}
          disabled={!canArrive}
          title={canArrive ? undefined : 'Produit pas encore entré en stock — saisissez d\'abord la facture d\'achat.'}
          className={`${base} ${canArrive
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        >
          <IcCheck size={11} /> Marquer arrivée
        </button>
      )}
      {/* Brand Kit §03 : « Bloquer » passé au kit SECONDAIRE — bordé encre
          (blanc, texte + bordure #111827) sur demande owner 2026-09-06. */}
      {r.status === 'arrived' && (
        <button onClick={handleBlock} className={`${base} btn-s`}>
          <IcLock size={11} /> Bloquer
        </button>
      )}
      {r.status === 'blocked' && (
        <button onClick={handleUnblock} className={`${base} bg-white border border-gray-300 text-gray-800 hover:bg-gray-50`}>
          <IcUnlock size={11} /> Débloquer
        </button>
      )}
      {(r.status === 'arrived' || r.status === 'blocked') && (
        <button onClick={handleSell} className={`${base} btn-p`}>
          <IcCalculator size={11} /> Vendre au client
        </button>
      )}
      {(r.status === 'pending' || r.status === 'arrived') && (
        <button onClick={handleCancel} className={`${base} ${neutral}`}>
          <IcClose size={11} /> Annuler
        </button>
      )}
    </div>
  );
}
